import React from "react";
import { ApolloClient } from "@apollo/client/core";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { useAuth0 } from "@auth0/auth0-react";
import possibleTypes from "possible_types.json";
import { ApolloProvider, InMemoryCache, split, HttpLink, ApolloLink } from "@apollo/client";
import { hasSubscription } from "@jumpn/utils-graphql";
import { createClient } from "graphql-ws";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";

const uri = `${process.env.REACT_APP_BASE_BACKEND_URL}/api`;

// We should probably use BatchHttpLink but burn that bridge later.
const httpLink = new HttpLink({ uri: uri });

export const ApolloSetup = (props) => {
  const { getAccessTokenSilently, error, loading, logout } = useAuth0();
  const [token, setToken] = React.useState();

  React.useEffect(() => {
    const callAPI = async () => {
      const token = await getAccessTokenSilently();
      setToken(token);
    };
    if (!loading && !error) {
      callAPI();
    }
  }, [loading, getAccessTokenSilently, error]);

  const setHeaders = setContext(async () => {
    const token = await getAccessTokenSilently();
    return {
      // Setting origin is probably done automatically but meh.
      headers: {
        Origin: window.location.origin,
        authorization: `Bearer ${token}`,
      },
    };
  });

  // 'Afterware' is very similar to a middleware, except that an afterware runs after a request has
  // been made. I think we could leverage this to deal with the situation where a nursery / user is
  // removed from the system, but they still have a local session. The server can return unauthorized
  // then we can catch that in afterware and log them out.

  // This does not actually do anything but log the error to the console, which is fine for dev
  // but is it a good idea for prod? Will it leak info? Who's to say.
  const logErrors = onError((stuff) => {
    if (process.env.NODE_ENV !== "production") {
      console.error("logErrors");
      console.log(stuff);
    }
    const { graphQLErrors, networkError } = stuff;
    if (graphQLErrors && process.env.NODE_ENV !== "production")
      graphQLErrors.forEach(({ message, locations, path }) =>
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        ),
      );
    if (networkError && networkError.statusCode === 401) {
      console.error("Logging out");
      logout();
      console.error(`[Unauthorized]: ${networkError}`);
    }
    if (networkError) console.error(`[Network error]: ${networkError}`);
  });

  // https://github.com/absinthe-graphql/absinthe-socket/issues/66
  const webSocketLink = new GraphQLWsLink(
    createClient({
      url: process.env.REACT_APP_GRAPHQL_SOCKET_URI,
      connectionParams: () => {
        return { token };
      },
    }),
  );

  const link = split(
    (operation) => hasSubscription(operation.query),
    ApolloLink.from([logErrors, setHeaders, webSocketLink]),
    ApolloLink.from([logErrors, setHeaders, httpLink]),
  );

  const client = new ApolloClient({
    connectToDevTools: true,
    link,
    cache: new InMemoryCache({ possibleTypes }),
  });

  return <ApolloProvider client={client}>{props.children}</ApolloProvider>;
};
